import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Pause, Play, RefreshCw } from 'lucide-react';
import type { ArchetypeId } from '@llura/types';

import { Button } from '@llura/ui';

import { useStrings } from '../../../hooks/useStrings';
import { useHaptic } from '../../../hooks/useHaptic';
import { getArchetype } from '../../../lib/archetypes';

interface Level1BreathProps {
  archetypeId: ArchetypeId;
  defaultDurationS?: number;
  onComplete: () => void;
}

type Phase = 'inhale' | 'hold' | 'exhale';

/**
 * Level 1 — Mind & Breath.
 *
 * A meditative breath circle paced by the chosen archetype's rhythm. The circle
 * grows on inhale, holds, shrinks on exhale; a haptic tick fires on every phase
 * change. Phase labels animate via key-based remount so each transition reads
 * crisp. Falls back to a static circle for users with `prefers-reduced-motion`.
 */
export function Level1Breath({
  archetypeId,
  defaultDurationS = 60,
  onComplete,
}: Level1BreathProps) {
  const { strings } = useStrings();
  const haptic = useHaptic();
  const rhythm = getArchetype(archetypeId).breathRhythmMs;
  const cycleMs = rhythm.inhale + rhythm.hold + rhythm.exhale;

  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(defaultDurationS);
  const [phase, setPhase] = useState<Phase>('inhale');
  const phaseStartRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  // 1-second countdown.
  useEffect(() => {
    if (!running) return;
    if (remaining <= 0) {
      setRunning(false);
      haptic.success();
      onComplete();
      return;
    }
    tickRef.current = window.setTimeout(() => {
      setRemaining((r) => r - 1);
    }, 1000);
    return () => {
      if (tickRef.current !== null) window.clearTimeout(tickRef.current);
    };
  }, [running, remaining, onComplete, haptic]);

  // Drive the breath phase off the high-resolution timer so it stays in sync
  // even if the JS thread stutters.
  useEffect(() => {
    if (!running) {
      phaseStartRef.current = null;
      return;
    }
    const start = performance.now();
    phaseStartRef.current = start;

    const step = (now: number) => {
      const elapsedInCycle = (now - start) % cycleMs;
      let next: Phase;
      if (elapsedInCycle < rhythm.inhale) {
        next = 'inhale';
      } else if (elapsedInCycle < rhythm.inhale + rhythm.hold) {
        next = rhythm.hold > 0 ? 'hold' : 'exhale';
      } else {
        next = 'exhale';
      }
      setPhase((prev) => {
        if (prev !== next) {
          haptic.tap();
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [running, cycleMs, rhythm.hold, rhythm.inhale, haptic]);

  const phaseLabel = useMemo(() => {
    if (phase === 'inhale') return strings.levels.breath.inhale;
    if (phase === 'hold') return strings.levels.breath.hold;
    return strings.levels.breath.exhale;
  }, [phase, strings.levels.breath]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  const reset = () => {
    setRunning(false);
    setRemaining(defaultDurationS);
    setPhase('inhale');
  };

  // Drive scale keyframes from rhythm fractions so the circle visibly tracks
  // the phases the user reads in the centre.
  const totalSeconds = cycleMs / 1000;
  const inhaleFrac = rhythm.inhale / cycleMs;
  const holdEndFrac = (rhythm.inhale + rhythm.hold) / cycleMs;
  const scaleTimes = rhythm.hold > 0 ? [0, inhaleFrac, holdEndFrac, 1] : [0, inhaleFrac, 1];
  const scaleFrames = rhythm.hold > 0 ? [0.6, 1, 1, 0.6] : [0.6, 1, 0.6];

  return (
    <div className="flex flex-col items-center gap-6 pt-2">
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
          {strings.levels.breath.label}
        </p>
        <h3 className="mt-1 font-serif text-2xl">{strings.levels.breath.title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{strings.levels.breath.hint}</p>
      </div>

      <div className="relative flex h-72 w-72 items-center justify-center">
        <motion.div
          aria-hidden
          className="absolute inset-6 rounded-full bg-primary/15 blur-2xl"
          animate={
            running
              ? { scale: scaleFrames, opacity: [0.45, 0.85, 0.85, 0.45].slice(0, scaleFrames.length) }
              : { scale: 1, opacity: 0.45 }
          }
          transition={{
            duration: totalSeconds,
            times: scaleTimes,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="relative flex h-56 w-56 items-center justify-center rounded-full border-2 border-primary/80 bg-card glow-primary"
          animate={running ? { scale: scaleFrames } : { scale: 1 }}
          transition={{
            duration: totalSeconds,
            times: scaleTimes,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <motion.span
            key={phase}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="font-serif text-3xl text-foreground"
            aria-live="polite"
          >
            {running ? phaseLabel : strings.levels.common.ready}
          </motion.span>
        </motion.div>
      </div>

      <div className="text-center font-mono text-4xl tracking-widest">
        {mm}:{ss}
      </div>

      <div className="flex w-full max-w-xs items-center gap-2">
        <Button
          onClick={() => setRunning((r) => !r)}
          className="flex-1 rounded-full bg-primary text-primary-foreground glow-primary"
          size="lg"
        >
          {running ? (
            <>
              <Pause className="h-4 w-4" /> {strings.levels.common.pause}
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />{' '}
              {remaining === defaultDurationS
                ? strings.levels.common.begin
                : strings.levels.common.resume}
            </>
          )}
        </Button>
        <Button
          onClick={reset}
          variant="outline"
          size="lg"
          className="rounded-full"
          aria-label={strings.levels.common.resetAria}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <Button
        variant="ghost"
        onClick={onComplete}
        className="text-sm text-muted-foreground hover:text-foreground"
      >
        {strings.levels.common.skip}
      </Button>
    </div>
  );
}
